// =====================================================
// Marketing Video Generator - ENHANCED
// Multi-Image Upload + AI Voice Narration
// Uses Kling AI Image-to-Video + ElevenLabs TTS
// =====================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const MarketingVideoAdmin = ({ user }) => {
  // Original state
  const [styles, setStyles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [config, setConfig] = useState(null);
  
  // Original form state (for single image mode)
  const [selectedStyle, setSelectedStyle] = useState('feature-showcase');
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [title, setTitle] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [generatingVideoId, setGeneratingVideoId] = useState(null);
  
  // NEW: Mode toggle
  const [mode, setMode] = useState('single'); // 'single' | 'multi'
  
  // NEW: Multi-image project state
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [narrationScript, setNarrationScript] = useState(null);
  const [multiTab, setMultiTab] = useState('upload'); // 'upload' | 'narration' | 'generate' | 'preview'
  const [generatingStates, setGeneratingStates] = useState({});
  const [merging, setMerging] = useState(false);
  const [finalVideoUrl, setFinalVideoUrl] = useState(null);
  
  const fileInputRef = useRef(null);
  const multiFileInputRef = useRef(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'x-user-id': user?.id?.toString() || ''
  });

  useEffect(() => {
    if (user?.id) {
      loadConfig();
      loadStyles();
      loadVideos();
      loadProjects();
    }
  }, [user?.id]);

  // Poll for video completion (single mode)
  useEffect(() => {
    if (generatingVideoId) {
      const pollInterval = setInterval(async () => {
        try {
          const response = await fetch(
            `${API_BASE}/video/marketing/status/${generatingVideoId}`,
            { headers: getAuthHeaders() }
          );
          const data = await response.json();
          
          if (data.video?.status === 'completed') {
            setGeneratingVideoId(null);
            setGenerating(false);
            loadVideos();
            alert('✅ Video generated successfully!');
          } else if (data.video?.status === 'failed') {
            setGeneratingVideoId(null);
            setGenerating(false);
            alert(`❌ Video generation failed: ${data.video?.error_message}`);
          }
        } catch (error) {
          console.error('Poll error:', error);
        }
      }, 10000);
      
      return () => clearInterval(pollInterval);
    }
  }, [generatingVideoId]);

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/video/config`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadStyles = async () => {
    try {
      const response = await fetch(`${API_BASE}/video/marketing/styles`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setStyles(data.styles || []);
      }
    } catch (error) {
      console.error('Error loading styles:', error);
    }
  };

  const loadVideos = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/video/marketing/list`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setVideos(data.videos || []);
      }
    } catch (error) {
      console.error('Error loading videos:', error);
    }
    setLoading(false);
  };

  // =====================================================
  // NEW: Project Management
  // =====================================================

  const loadProjects = async () => {
    try {
      const res = await fetch(`${API_BASE}/video/projects`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadProject = async (projectId) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/video/projects/${projectId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCurrentProject(data.project);
        setScenes(data.scenes || []);
        setFinalVideoUrl(data.project?.final_video_url || null);
        if (data.project?.narration_script) {
          try {
            setNarrationScript(
              typeof data.project.narration_script === 'string'
                ? JSON.parse(data.project.narration_script)
                : data.project.narration_script
            );
          } catch {
            setNarrationScript(null);
          }
        } else {
          setNarrationScript(null);
        }
      }
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setLoading(false);
    }
  };

  // Force check status of all scenes (calls backend which checks Kling API)
  const forceCheckAllScenes = async () => {
    if (!currentProject?.project_id || scenes.length === 0) {
      alert('No project or scenes to check');
      return;
    }
    
    setLoading(true);
    console.log('[ForceCheck] Checking all scenes...');
    
    try {
      const pendingScenes = scenes.filter(s => !s.video_url && s.kling_task_id);
      
      if (pendingScenes.length === 0) {
        alert('All scenes already have videos or no tasks pending');
        await loadProject(currentProject.project_id);
        return;
      }
      
      let completed = 0;
      let stillProcessing = 0;
      let errors = 0;
      
      for (const scene of pendingScenes) {
        try {
          console.log(`[ForceCheck] Checking scene ${scene.id}...`);
          const res = await fetch(
            `${API_BASE}/video/projects/${currentProject.project_id}/scenes/${scene.id}/status`,
            { headers: getAuthHeaders() }
          );
          const data = await res.json();
          console.log(`[ForceCheck] Scene ${scene.id}: ${data.status}`);
          
          if (data.status === 'completed') {
            completed++;
          } else if (data.status === 'processing') {
            stillProcessing++;
          } else {
            errors++;
          }
        } catch (err) {
          console.error(`[ForceCheck] Error checking scene ${scene.id}:`, err);
          errors++;
        }
      }
      
      // Reload project to get updated data
      await loadProject(currentProject.project_id);
      
      alert(`Status Check Complete!\n\n✅ Completed: ${completed}\n⏳ Still Processing: ${stillProcessing}\n❌ Errors: ${errors}`);
      
    } catch (error) {
      console.error('[ForceCheck] Error:', error);
      alert('Error checking scenes: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async () => {
    const projectTitle = prompt('Enter video project title:');
    if (!projectTitle) return;
    const topic = prompt('Enter topic for AI narration (e.g., "InvestorQ platform features"):') || projectTitle;

    try {
      const res = await fetch(`${API_BASE}/video/projects`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title: projectTitle, topic, style: 'professional' })
      });
      if (res.ok) {
        const data = await res.json();
        await loadProjects();
        loadProject(data.project.project_id);
        setMultiTab('upload');
      }
    } catch (error) {
      alert('Failed to create project: ' + error.message);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Delete this project and all its scenes?')) return;
    try {
      await fetch(`${API_BASE}/video/projects/${projectId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      setCurrentProject(null);
      setScenes([]);
      setNarrationScript(null);
      loadProjects();
    } catch (error) {
      alert('Failed to delete: ' + error.message);
    }
  };

  // =====================================================
  // NEW: Multi-File Upload with Drag & Drop
  // =====================================================

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleMultipleFiles(e.dataTransfer.files);
    }
  }, [currentProject]);

  const handleMultiFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleMultipleFiles(e.target.files);
    }
  };

  const handleMultipleFiles = async (files) => {
    if (!currentProject) {
      alert('Please create or select a project first');
      return;
    }

    const fileArray = Array.from(files);
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const validFiles = fileArray.filter(file => {
      if (!validTypes.includes(file.type)) {
        console.log(`Skipping invalid type: ${file.type}`);
        return false;
      }
      if (file.size > 50 * 1024 * 1024) {
        console.log(`Skipping large file: ${file.name}`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      alert('No valid image files. Supported: JPEG, PNG, GIF, WebP (max 50MB each)');
      return;
    }

    setUploadProgress({ current: 0, total: validFiles.length, status: 'uploading' });

    try {
      const formData = new FormData();
      validFiles.forEach(file => formData.append('images', file));

      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/scenes/upload`, {
        method: 'POST',
        headers: { 'x-user-id': user?.id?.toString() || '' },
        body: formData
      });

      const data = await res.json();
      
      if (data.success) {
        setUploadProgress({ 
          current: data.uploaded, 
          total: validFiles.length, 
          status: 'complete',
          errors: data.errors 
        });
        await loadProject(currentProject.project_id);
        setTimeout(() => setUploadProgress(null), 3000);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress({ status: 'error', error: error.message });
      setTimeout(() => setUploadProgress(null), 5000);
    }
  };

  const deleteScene = async (sceneId) => {
    if (!window.confirm('Delete this scene?')) return;
    try {
      await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/scenes/${sceneId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      await loadProject(currentProject.project_id);
    } catch (error) {
      alert('Failed to delete scene');
    }
  };

  // =====================================================
  // NEW: AI Narration Generation
  // =====================================================

  const generateNarrationScript = async () => {
    if (scenes.length === 0) {
      alert('Upload some images first!');
      return;
    }
    
    setGeneratingStates(prev => ({ ...prev, script: true }));
    
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/narration/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          topic: currentProject.topic || currentProject.title,
          style: 'professional'
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setNarrationScript(data.script);
        alert('✅ Narration script generated! Review it below.');
      } else {
        throw new Error(data.error || 'Failed to generate script');
      }
    } catch (error) {
      alert('Failed to generate script: ' + error.message);
    } finally {
      setGeneratingStates(prev => ({ ...prev, script: false }));
    }
  };

  const generateNarrationAudio = async () => {
    if (!narrationScript) {
      alert('Generate a script first!');
      return;
    }
    
    setGeneratingStates(prev => ({ ...prev, audio: true }));
    
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/narration/audio`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await res.json();
      
      if (data.success) {
        setCurrentProject(prev => ({ ...prev, narration_audio_url: data.audioUrl }));
        alert('✅ Voice narration generated!');
      } else {
        throw new Error(data.error || 'Failed to generate audio');
      }
    } catch (error) {
      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingStates(prev => ({ ...prev, audio: false }));
    }
  };

  // =====================================================
  // NEW: Scene Video Generation
  // =====================================================

  const generateSceneVideo = async (sceneId, customPrompt) => {
    setGeneratingStates(prev => ({ ...prev, [sceneId]: true }));
    
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/scenes/${sceneId}/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          prompt: customPrompt || 'Subtle cinematic camera movement, professional quality',
          mode: 'std',
          duration: '5'
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        alert(`🎬 Video generation started! Task: ${data.taskId}\n\nThis takes 3-5 minutes.`);
        pollSceneStatus(sceneId);
      } else {
        throw new Error(data.error || 'Failed to start generation');
      }
    } catch (error) {
      alert('Failed to generate video: ' + error.message);
      setGeneratingStates(prev => ({ ...prev, [sceneId]: false }));
    }
  };

  const generateAllVideos = async (animationStyle = 'subtle') => {
    const pendingScenes = scenes.filter(s => !s.video_url && s.scene_type === 'image');
    
    if (pendingScenes.length === 0) {
      alert('All scenes already have videos!');
      return;
    }
    
    const styleDescriptions = {
      'dynamic': '🎬 Dynamic - Full cinematic animation (best for photos, products)',
      'subtle': '📱 Subtle - Minimal motion (best for UI screenshots)',
      'cinematic': '🎥 Cinematic - Dramatic reveal effects'
    };
    
    if (!window.confirm(`Generate ${pendingScenes.length} videos with:\n\n${styleDescriptions[animationStyle]}\n\nThis will take ${pendingScenes.length * 3}-${pendingScenes.length * 5} minutes.`)) {
      return;
    }

    // Clear any existing polls
    activePolls.current.clear();
    
    setGeneratingStates(prev => ({ ...prev, all: true }));
    
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/generate-all`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          mode: 'std', 
          duration: '5',
          animationStyle: animationStyle
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        const started = data.results.filter(r => r.status === 'processing').length;
        alert(`🎬 Started ${started} video generations!\n\nStyle: ${animationStyle}\nModel: ${data.results[0]?.model || 'kling'}\n\nThey will complete in 3-5 minutes each.`);
        
        // Set individual generating states and start polling
        const newStates = { all: false };
        data.results.forEach(r => {
          if (r.status === 'processing') {
            newStates[r.sceneId] = true;
            // Start polling with small delay between each
            setTimeout(() => pollSceneStatus(r.sceneId), data.results.indexOf(r) * 500);
          }
        });
        setGeneratingStates(prev => ({ ...prev, ...newStates }));
      } else {
        throw new Error(data.error || 'Failed to start batch generation');
      }
    } catch (error) {
      alert('Failed to generate videos: ' + error.message);
      setGeneratingStates(prev => ({ ...prev, all: false }));
    }
  };

  // Track active polls to prevent duplicates
  const activePolls = useRef(new Set());
  
  const pollSceneStatus = async (sceneId, attempts = 0) => {
    // Prevent duplicate polling for same scene
    if (activePolls.current.has(sceneId)) {
      console.log(`[Poll] Already polling scene ${sceneId}, skipping`);
      return;
    }
    
    if (attempts > 60) {
      console.log(`[Poll] Scene ${sceneId} timed out after 60 attempts`);
      setGeneratingStates(prev => ({ ...prev, [sceneId]: false }));
      activePolls.current.delete(sceneId);
      return;
    }
    
    // Mark as actively polling
    if (attempts === 0) {
      activePolls.current.add(sceneId);
    }
    
    try {
      const res = await fetch(
        `${API_BASE}/video/projects/${currentProject.project_id}/scenes/${sceneId}/status`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      
      console.log(`[Poll] Scene ${sceneId} status: ${data.status} (attempt ${attempts + 1})`);
      
      if (data.status === 'completed') {
        console.log(`[Poll] ✅ Scene ${sceneId} completed!`);
        setGeneratingStates(prev => ({ ...prev, [sceneId]: false }));
        activePolls.current.delete(sceneId);
        // Reload project to get updated scene data
        await loadProject(currentProject.project_id);
        return;
      }
      
      if (data.status === 'error' || data.status === 'failed') {
        console.log(`[Poll] ❌ Scene ${sceneId} failed: ${data.error}`);
        setGeneratingStates(prev => ({ ...prev, [sceneId]: false }));
        activePolls.current.delete(sceneId);
        alert(`Scene ${sceneId} failed: ${data.error || 'Unknown error'}`);
        return;
      }
      
      // Keep polling (10 second interval)
      setTimeout(() => pollSceneStatus(sceneId, attempts + 1), 10000);
    } catch (error) {
      console.error(`[Poll] Error for scene ${sceneId}:`, error);
      // On error, retry a few times then give up
      if (attempts < 5) {
        setTimeout(() => pollSceneStatus(sceneId, attempts + 1), 10000);
      } else {
        setGeneratingStates(prev => ({ ...prev, [sceneId]: false }));
        activePolls.current.delete(sceneId);
      }
    }
  };

  // Merge all videos with narration audio
  const mergeVideosWithAudio = async () => {
    const videosReady = scenes.filter(s => s.video_url).length;
    
    if (videosReady === 0) {
      alert('No videos generated yet. Generate scene videos first.');
      return;
    }
    
    if (videosReady < scenes.length) {
      if (!window.confirm(`Only ${videosReady}/${scenes.length} videos are ready.\n\nMerge with available videos?`)) {
        return;
      }
    }
    
    setMerging(true);
    
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/merge`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await res.json();
      
      if (data.success) {
        setFinalVideoUrl(data.finalVideoUrl);
        alert(`✅ Video merged successfully!\n\n${data.scenesCount} scenes${data.hasAudio ? ' + narration audio' : ''}`);
      } else {
        throw new Error(data.error || data.message || 'Merge failed');
      }
    } catch (error) {
      console.error('Merge error:', error);
      alert('Failed to merge videos: ' + error.message);
    } finally {
      setMerging(false);
    }
  };

  // Create Ken Burns slideshow (clean video from images, no AI)
  const createKenBurnsSlideshow = async () => {
    if (scenes.length === 0) {
      alert('No images uploaded yet. Upload images first.');
      return;
    }
    
    if (!currentProject.narration_audio_url) {
      if (!window.confirm('No narration audio found.\n\nCreate slideshow without audio?')) {
        return;
      }
    }
    
    setMerging(true);
    
    try {
      const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/slideshow`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await res.json();
      
      if (data.success) {
        setFinalVideoUrl(data.finalVideoUrl);
        alert(`✅ Slideshow created successfully!\n\n${data.scenesCount} images\n${Math.round(data.totalDuration)} seconds${data.hasAudio ? '\n+ narration audio' : ''}`);
        // Reload to update UI
        await loadProject(currentProject.project_id);
      } else {
        throw new Error(data.error || data.message || 'Slideshow creation failed');
      }
    } catch (error) {
      console.error('Slideshow error:', error);
      alert('Failed to create slideshow: ' + error.message);
    } finally {
      setMerging(false);
    }
  };

  // =====================================================
  // ORIGINAL: Single Image Upload & Generation
  // =====================================================

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, etc.)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('Image is too large for direct upload (max 5MB).\n\nTip: Upload your image to Imgur or another host, then paste the URL.');
      return;
    }
    
    setImagePreview(null);
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const maxSize = 1920;
          let width = img.width;
          let height = img.height;
          
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = (height / width) * maxSize;
              width = maxSize;
            } else {
              width = (width / height) * maxSize;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.85);
          const base64Data = compressedBase64.split(',')[1];
          
          setImagePreview(compressedBase64);
          
          try {
            const response = await fetch(`${API_BASE}/video/marketing/upload-image`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify({
                imageBase64: base64Data,
                fileName: file.name,
                contentType: 'image/jpeg'
              })
            });
            
            if (!response.ok) {
              if (response.status === 413) {
                alert('Image still too large after compression.\n\nPlease use a smaller image or paste a URL instead.');
                return;
              }
              throw new Error(`Upload failed: ${response.status}`);
            }
            
            const data = await response.json();
            if (data.success) {
              setImageUrl(data.imageUrl);
            } else {
              alert('Failed to upload image: ' + (data.error || 'Unknown error'));
            }
          } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload image. Try pasting a URL instead.');
          }
        };
        img.src = e.target.result;
      } catch (error) {
        console.error('Image processing error:', error);
        alert('Failed to process image');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!imageUrl) {
      alert('Please upload an image first');
      return;
    }
    
    setGenerating(true);
    
    try {
      const response = await fetch(`${API_BASE}/video/marketing/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          imageUrl,
          styleId: selectedStyle,
          aspectRatio,
          title: title || `Marketing Video - ${new Date().toLocaleDateString()}`
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGeneratingVideoId(data.videoId);
        alert(`🎬 Video generation started!\n\nVideo ID: ${data.videoId}\n\nThis will take 3-5 minutes.`);
        
        setImageUrl('');
        setImagePreview(null);
        setTitle('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        alert('Error: ' + data.error);
        setGenerating(false);
      }
      
    } catch (error) {
      alert('Error: ' + error.message);
      setGenerating(false);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    
    try {
      await fetch(`${API_BASE}/video/marketing/${videoId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      loadVideos();
    } catch (error) {
      alert('Error deleting video');
    }
  };

  // Show login prompt if no user
  if (!user?.id) {
    return (
      <div style={{ 
        minHeight: '50vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '1rem',
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <h2 style={{ color: '#f87171', margin: '0 0 1rem' }}>🔒 Admin Access Required</h2>
          <p style={{ color: '#94a3b8', margin: 0 }}>
            Please log in with an admin account.
          </p>
        </div>
      </div>
    );
  }

  const selectedStyleConfig = styles.find(s => s.id === selectedStyle);

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)', 
      color: '#e2e8f0',
      padding: '1rem'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>🎬 Marketing Video Generator</h1>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem' }}>
            Create professional marketing videos from screenshots with AI animation & voice narration
          </p>
        </div>

        {/* Configuration Status */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          flexWrap: 'wrap'
        }}>
          <span style={{
            padding: '0.25rem 0.75rem',
            background: config?.klingConfigured ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
            border: `1px solid ${config?.klingConfigured ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            borderRadius: '0.5rem',
            fontSize: '0.75rem'
          }}>
            {config?.klingConfigured ? '✅' : '❌'} Kling AI
          </span>
          <span style={{
            padding: '0.25rem 0.75rem',
            background: config?.openaiConfigured ? 'rgba(34, 197, 94, 0.2)' : 'rgba(245, 158, 11, 0.2)',
            border: `1px solid ${config?.openaiConfigured ? 'rgba(34, 197, 94, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
            borderRadius: '0.5rem',
            fontSize: '0.75rem'
          }}>
            {config?.openaiConfigured ? '✅' : '⚠️'} Voice (OpenAI TTS)
          </span>
          <span style={{
            padding: '0.25rem 0.75rem',
            background: config?.r2Configured ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
            border: `1px solid ${config?.r2Configured ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            borderRadius: '0.5rem',
            fontSize: '0.75rem'
          }}>
            {config?.r2Configured ? '✅' : '❌'} R2 Storage
          </span>
        </div>

        {/* Mode Toggle */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
          <button
            onClick={() => setMode('single')}
            style={{
              padding: '0.75rem 1.5rem',
              background: mode === 'single' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(30, 41, 59, 0.6)',
              border: mode === 'single' ? 'none' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: mode === 'single' ? '600' : '400'
            }}
          >
            📸 Single Image
          </button>
          <button
            onClick={() => setMode('multi')}
            style={{
              padding: '0.75rem 1.5rem',
              background: mode === 'multi' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(30, 41, 59, 0.6)',
              border: mode === 'multi' ? 'none' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: mode === 'multi' ? '600' : '400'
            }}
          >
            🎬 Multi-Image + Voice
          </button>
        </div>

        {/* =====================================================
            SINGLE IMAGE MODE (Original)
            ===================================================== */}
        {mode === 'single' && (
          <>
            {/* Generator Form */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.6)',
              borderRadius: '1rem',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <h2 style={{ margin: '0 0 1rem', fontSize: '1.125rem' }}>📤 Create Single Video</h2>
              
              <div style={{ display: 'grid', gap: '1rem' }}>
                {/* Image Input */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#94a3b8' }}>
                    1. Add Screenshot or Image
                  </label>
                  
                  <div style={{ marginBottom: '0.75rem' }}>
                    <input
                      type="text"
                      value={imageUrl}
                      onChange={(e) => {
                        setImageUrl(e.target.value);
                        if (e.target.value.startsWith('http')) {
                          setImagePreview(e.target.value);
                        }
                      }}
                      placeholder="Paste image URL..."
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(15, 23, 42, 0.8)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '0.5rem',
                        color: '#e2e8f0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  
                  <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.75rem', marginBottom: '0.75rem' }}>
                    — OR upload file —
                  </div>
                  
                  <div style={{
                    border: '2px dashed rgba(99, 102, 241, 0.3)',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    textAlign: 'center',
                    background: 'rgba(99, 102, 241, 0.05)',
                    cursor: 'pointer'
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  >
                    {imagePreview ? (
                      <div>
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '0.5rem', marginBottom: '0.5rem' }} 
                        />
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#22c55e' }}>✅ Image ready</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageUrl('');
                            setImagePreview(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          style={{
                            marginTop: '0.5rem',
                            padding: '0.25rem 0.75rem',
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '0.25rem',
                            color: '#f87171',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          ✕ Clear
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>📸</div>
                        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem' }}>Click to upload</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
                
                {/* Style Selection */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#94a3b8' }}>
                    2. Select Animation Style
                  </label>
                  <select
                    value={selectedStyle}
                    onChange={(e) => setSelectedStyle(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '0.5rem',
                      color: '#e2e8f0',
                      fontSize: '1rem'
                    }}
                  >
                    <optgroup label="Platform Showcases">
                      <option value="feature-showcase">🖥️ Feature Showcase - Smooth zoom & pan</option>
                      <option value="data-visualization">📊 Data Visualization - Charts come alive</option>
                      <option value="cinematic-reveal">🎬 Cinematic Reveal - Dramatic entrance</option>
                      <option value="hover-interaction">👆 Hover Interaction - UI simulation</option>
                      <option value="ambient-glow">✨ Ambient Glow - Subtle lighting</option>
                    </optgroup>
                    <optgroup label="Social Media">
                      <option value="social-instagram">📱 Instagram Story/Reel</option>
                      <option value="social-linkedin">💼 LinkedIn Professional</option>
                    </optgroup>
                  </select>
                </div>
                
                {/* Aspect Ratio */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#94a3b8' }}>
                    3. Aspect Ratio
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {[
                      { value: '16:9', label: '16:9 Landscape', icon: '🖥️' },
                      { value: '9:16', label: '9:16 Portrait', icon: '📱' },
                      { value: '1:1', label: '1:1 Square', icon: '⬜' }
                    ].map(ratio => (
                      <button
                        key={ratio.value}
                        onClick={() => setAspectRatio(ratio.value)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: aspectRatio === ratio.value ? 'rgba(99, 102, 241, 0.3)' : 'rgba(15, 23, 42, 0.5)',
                          border: `1px solid ${aspectRatio === ratio.value ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255,255,255,0.1)'}`,
                          borderRadius: '0.5rem',
                          color: aspectRatio === ratio.value ? '#a5b4fc' : '#94a3b8',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        {ratio.icon} {ratio.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Title */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#94a3b8' }}>
                    4. Video Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Alpha Intelligence Dashboard Demo"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '0.5rem',
                      color: '#e2e8f0',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                
                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={generating || !imageUrl || !config?.klingConfigured}
                  style={{
                    width: '100%',
                    padding: '1rem 1.5rem',
                    background: generating || !imageUrl ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontWeight: '600',
                    cursor: generating || !imageUrl ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    marginTop: '0.5rem'
                  }}
                >
                  {generating ? '⏳ Generating Video (3-5 min)...' : '🎬 Generate Marketing Video'}
                </button>
              </div>
            </div>

            {/* Generated Videos List */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.6)',
              borderRadius: '1rem',
              padding: '1.5rem',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.125rem' }}>📺 Your Videos ({videos.length})</h2>
                <button
                  onClick={loadVideos}
                  disabled={loading}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'rgba(59, 130, 246, 0.2)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '0.5rem',
                    color: '#60a5fa',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  🔄 Refresh
                </button>
              </div>
              
              {loading ? (
                <p style={{ textAlign: 'center', color: '#94a3b8' }}>Loading...</p>
              ) : videos.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                  <p style={{ fontSize: '2rem', margin: '0 0 0.5rem' }}>🎥</p>
                  <p style={{ margin: 0 }}>No videos yet. Create your first one above!</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
                  {videos.map(video => (
                    <div key={video.video_id} style={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.05)' }}>
                      {video.status === 'completed' && video.video_url ? (
                        <video src={video.video_url} controls style={{ width: '100%', display: 'block' }} poster={video.source_image_url} />
                      ) : video.status === 'failed' ? (
                        <div style={{ padding: '2rem', textAlign: 'center', background: 'rgba(239, 68, 68, 0.1)' }}>
                          <p style={{ color: '#ef4444', margin: 0 }}>❌ Generation failed</p>
                        </div>
                      ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', background: 'rgba(245, 158, 11, 0.1)' }}>
                          <p style={{ color: '#fbbf24', margin: 0 }}>⏳ Generating...</p>
                        </div>
                      )}
                      
                      <div style={{ padding: '1rem' }}>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem' }}>{video.title || 'Untitled'}</h3>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                          <span style={{ padding: '0.125rem 0.5rem', background: 'rgba(139, 92, 246, 0.2)', borderRadius: '0.25rem', fontSize: '0.7rem', color: '#a78bfa' }}>
                            {video.style_id || 'custom'}
                          </span>
                          <span style={{
                            padding: '0.125rem 0.5rem',
                            background: video.status === 'completed' ? 'rgba(34, 197, 94, 0.2)' : video.status === 'failed' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                            borderRadius: '0.25rem',
                            fontSize: '0.7rem',
                            color: video.status === 'completed' ? '#22c55e' : video.status === 'failed' ? '#ef4444' : '#fbbf24'
                          }}>
                            {video.status}
                          </span>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {video.video_url && (
                            <a href={video.video_url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, padding: '0.5rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '0.375rem', color: '#60a5fa', textDecoration: 'none', textAlign: 'center', fontSize: '0.8rem' }}>
                              📥 Download
                            </a>
                          )}
                          <button onClick={() => deleteVideo(video.video_id)} style={{ padding: '0.5rem 0.75rem', background: 'rgba(107, 114, 128, 0.2)', border: '1px solid rgba(107, 114, 128, 0.3)', borderRadius: '0.375rem', color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem' }}>
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* =====================================================
            MULTI-IMAGE + VOICE MODE (New)
            ===================================================== */}
        {mode === 'multi' && (
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {/* Projects Sidebar */}
            <div style={{ width: '250px', background: 'rgba(30, 41, 59, 0.6)', borderRadius: '1rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.08)', height: 'fit-content' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ margin: 0, fontSize: '0.9rem' }}>📁 Projects</h3>
                <button onClick={createProject} style={{ padding: '0.4rem 0.75rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '0.375rem', color: 'white', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>+ New</button>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {projects.map(p => (
                  <div
                    key={p.project_id}
                    onClick={() => loadProject(p.project_id)}
                    style={{
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      background: currentProject?.project_id === p.project_id ? 'rgba(245, 158, 11, 0.2)' : 'rgba(15, 23, 42, 0.5)',
                      border: currentProject?.project_id === p.project_id ? '1px solid rgba(245, 158, 11, 0.5)' : '1px solid transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontWeight: '600', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{p.title}</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{p.status} • {new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
                {projects.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '1rem', color: '#64748b', fontSize: '0.8rem' }}>
                    No projects yet.<br/>Click "+ New" to start!
                  </div>
                )}
              </div>
            </div>

            {/* Main Content Area */}
            <div style={{ flex: 1, minWidth: '300px', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '1rem', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.08)' }}>
              {!currentProject ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎬</div>
                  <h2 style={{ margin: '0 0 0.5rem', color: '#e2e8f0' }}>Create a Video Project</h2>
                  <p style={{ marginBottom: '1.5rem' }}>Upload multiple images, add AI voice narration, and generate professional marketing videos.</p>
                  <button onClick={createProject} style={{ padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>🎬 Create Project</button>
                </div>
              ) : (
                <>
                  {/* Project Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{currentProject.title}</h2>
                      <p style={{ color: '#94a3b8', marginTop: '0.25rem', fontSize: '0.85rem' }}>Topic: {currentProject.topic || 'Not set'}</p>
                    </div>
                    <button onClick={() => deleteProject(currentProject.project_id)} style={{ padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '0.5rem', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem' }}>🗑️ Delete</button>
                  </div>

                  {/* Tabs */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                    {[
                      { id: 'upload', label: '📤 Upload Images', count: scenes.length },
                      { id: 'narration', label: '🎤 Voice Narration', status: narrationScript ? '✓' : '' },
                      { id: 'generate', label: '🎬 Generate Videos', count: scenes.filter(s => s.video_url).length },
                      { id: 'preview', label: '👁️ Preview' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setMultiTab(tab.id)}
                        style={{
                          padding: '0.6rem 1rem',
                          background: multiTab === tab.id ? 'rgba(245, 158, 11, 0.2)' : 'rgba(15, 23, 42, 0.5)',
                          border: multiTab === tab.id ? '1px solid rgba(245, 158, 11, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '0.5rem',
                          color: multiTab === tab.id ? '#fbbf24' : '#94a3b8',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        {tab.label}
                        {tab.count !== undefined && <span style={{ marginLeft: '0.5rem', opacity: 0.7 }}>({tab.count})</span>}
                        {tab.status && <span style={{ marginLeft: '0.5rem', color: '#22c55e' }}>{tab.status}</span>}
                      </button>
                    ))}
                  </div>

                  {/* UPLOAD TAB */}
                  {multiTab === 'upload' && (
                    <div>
                      {/* Drag & Drop Zone */}
                      <div
                        style={{
                          border: `2px dashed ${dragActive ? '#f59e0b' : 'rgba(245, 158, 11, 0.4)'}`,
                          borderRadius: '1rem',
                          padding: '2rem',
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: dragActive ? 'rgba(245, 158, 11, 0.1)' : 'rgba(15, 23, 42, 0.3)',
                          marginBottom: '1.5rem',
                          transition: 'all 0.2s'
                        }}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => multiFileInputRef.current?.click()}
                      >
                        <input
                          ref={multiFileInputRef}
                          type="file"
                          multiple
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handleMultiFileInput}
                          style={{ display: 'none' }}
                        />
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📁</div>
                        <h3 style={{ margin: '0 0 0.5rem' }}>Drop multiple images here</h3>
                        <p style={{ color: '#64748b', margin: 0, fontSize: '0.85rem' }}>or click to browse (JPEG, PNG, GIF, WebP)</p>
                        <p style={{ color: '#f59e0b', margin: '0.5rem 0 0', fontSize: '0.8rem' }}>💡 Tip: Ctrl/Cmd + Click to select multiple files</p>
                      </div>

                      {/* Upload Progress */}
                      {uploadProgress && (
                        <div style={{ padding: '1rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '0.5rem', marginBottom: '1rem', textAlign: 'center' }}>
                          {uploadProgress.status === 'uploading' && <p style={{ margin: 0, color: '#fbbf24' }}>⏳ Uploading... {uploadProgress.current}/{uploadProgress.total}</p>}
                          {uploadProgress.status === 'complete' && <p style={{ margin: 0, color: '#22c55e' }}>✅ Uploaded {uploadProgress.current} images!</p>}
                          {uploadProgress.status === 'error' && <p style={{ margin: 0, color: '#ef4444' }}>❌ {uploadProgress.error}</p>}
                        </div>
                      )}

                      {/* Scenes Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem' }}>
                        {scenes.map((scene, index) => (
                          <div key={scene.id} style={{ position: 'relative', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', background: 'rgba(0,0,0,0.7)', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.7rem', zIndex: 1 }}>
                              Scene {index + 1}
                            </div>
                            {scene.source_url && (
                              <img src={scene.source_url} alt={`Scene ${index + 1}`} style={{ width: '100%', height: '120px', objectFit: 'cover' }} />
                            )}
                            <div style={{ padding: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>🖼️</span>
                              <span style={{
                                fontSize: '0.65rem',
                                padding: '0.15rem 0.4rem',
                                borderRadius: '0.25rem',
                                background: scene.video_url ? 'rgba(34, 197, 94, 0.2)' : 'rgba(100, 116, 139, 0.3)',
                                color: scene.video_url ? '#22c55e' : '#94a3b8'
                              }}>
                                {scene.video_url ? '✓ Video' : 'Pending'}
                              </span>
                            </div>
                            <button
                              onClick={() => deleteScene(scene.id)}
                              style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'rgba(239, 68, 68, 0.8)', border: 'none', borderRadius: '50%', width: '20px', height: '20px', color: 'white', cursor: 'pointer', fontSize: '0.8rem', lineHeight: '1' }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      {scenes.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                          No images yet. Upload some above!
                        </div>
                      )}
                    </div>
                  )}

                  {/* NARRATION TAB */}
                  {multiTab === 'narration' && (
                    <div>
                      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={generateNarrationScript}
                          disabled={generatingStates.script || scenes.length === 0}
                          style={{
                            padding: '0.75rem 1.5rem',
                            background: generatingStates.script ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            border: 'none',
                            borderRadius: '0.5rem',
                            color: 'white',
                            cursor: generatingStates.script ? 'not-allowed' : 'pointer',
                            fontWeight: '600'
                          }}
                        >
                          {generatingStates.script ? '⏳ Generating...' : '📝 Generate AI Script'}
                        </button>
                        
                        {narrationScript && (
                          <button
                            onClick={generateNarrationAudio}
                            disabled={generatingStates.audio || !config?.openaiConfigured}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: generatingStates.audio ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #10b981, #059669)',
                              border: 'none',
                              borderRadius: '0.5rem',
                              color: 'white',
                              cursor: generatingStates.audio || !config?.openaiConfigured ? 'not-allowed' : 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            {generatingStates.audio ? '⏳ Generating...' : '🎤 Generate Voice Audio'}
                          </button>
                        )}
                      </div>

                      {!config?.openaiConfigured && (
                        <div style={{ padding: '1rem', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                          <p style={{ margin: 0, color: '#fbbf24', fontSize: '0.85rem' }}>
                            ⚠️ OpenAI not configured. Add OPENAI_API_KEY to enable voice generation.
                          </p>
                        </div>
                      )}

                      {narrationScript && (
                        <div style={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1rem' }}>
                          <h3 style={{ margin: '0 0 1rem' }}>📜 Generated Script</h3>
                          
                          <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <h4 style={{ margin: '0 0 0.5rem', color: '#fbbf24' }}>Title</h4>
                            <p style={{ margin: 0 }}>{narrationScript.title}</p>
                          </div>
                          
                          <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <h4 style={{ margin: '0 0 0.5rem', color: '#fbbf24' }}>Introduction</h4>
                            <p style={{ margin: 0 }}>{narrationScript.introduction}</p>
                          </div>
                          
                          {narrationScript.scenes?.map((s, i) => (
                            <div key={i} style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                              <h4 style={{ margin: '0 0 0.25rem', color: '#94a3b8', fontSize: '0.85rem' }}>Scene {s.sceneNumber}</h4>
                              <p style={{ margin: 0, fontSize: '0.9rem' }}>{s.narration}</p>
                              <small style={{ color: '#64748b' }}>~{s.duration_estimate_seconds}s</small>
                            </div>
                          ))}
                          
                          <div>
                            <h4 style={{ margin: '0 0 0.5rem', color: '#fbbf24' }}>Conclusion</h4>
                            <p style={{ margin: 0 }}>{narrationScript.conclusion}</p>
                          </div>
                        </div>
                      )}

                      {currentProject.narration_audio_url && (
                        <div style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '0.75rem', padding: '1rem' }}>
                          <h3 style={{ margin: '0 0 0.75rem' }}>🎧 Voice Narration Ready!</h3>
                          <audio controls src={currentProject.narration_audio_url} style={{ width: '100%' }} />
                          <a href={currentProject.narration_audio_url} download style={{ display: 'inline-block', marginTop: '0.75rem', color: '#60a5fa', textDecoration: 'none', fontSize: '0.85rem' }}>
                            📥 Download Audio
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* GENERATE TAB */}
                  {multiTab === 'generate' && (
                    <div>
                      {/* Animation Style Selection */}
                      <div style={{ 
                        marginBottom: '1.5rem', 
                        padding: '1rem', 
                        background: 'rgba(99, 102, 241, 0.1)', 
                        borderRadius: '0.75rem',
                        border: '1px solid rgba(99, 102, 241, 0.2)'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem', color: '#a5b4fc' }}>🎨 Choose Animation Style</h4>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => generateAllVideos('subtle')}
                            disabled={generatingStates.all || scenes.filter(s => !s.video_url).length === 0}
                            style={{
                              padding: '0.75rem 1rem',
                              background: generatingStates.all ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                              border: 'none',
                              borderRadius: '0.5rem',
                              color: 'white',
                              cursor: generatingStates.all ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              fontSize: '0.85rem'
                            }}
                          >
                            📱 Subtle (UI/Screenshots)
                          </button>
                          <button
                            onClick={() => generateAllVideos('dynamic')}
                            disabled={generatingStates.all || scenes.filter(s => !s.video_url).length === 0}
                            style={{
                              padding: '0.75rem 1rem',
                              background: generatingStates.all ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                              border: 'none',
                              borderRadius: '0.5rem',
                              color: 'white',
                              cursor: generatingStates.all ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              fontSize: '0.85rem'
                            }}
                          >
                            🎬 Dynamic (Photos/Products)
                          </button>
                          <button
                            onClick={() => generateAllVideos('cinematic')}
                            disabled={generatingStates.all || scenes.filter(s => !s.video_url).length === 0}
                            style={{
                              padding: '0.75rem 1rem',
                              background: generatingStates.all ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #ec4899, #be185d)',
                              border: 'none',
                              borderRadius: '0.5rem',
                              color: 'white',
                              cursor: generatingStates.all ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              fontSize: '0.85rem'
                            }}
                          >
                            🎥 Cinematic (Dramatic)
                          </button>
                        </div>
                        <p style={{ margin: '0.75rem 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>
                          💡 <strong>Subtle</strong> = minimal motion, keeps text readable | 
                          <strong> Dynamic</strong> = full animation | 
                          <strong> Cinematic</strong> = dramatic reveals
                        </p>
                      </div>

                      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={forceCheckAllScenes}
                          disabled={loading}
                          style={{
                            padding: '0.75rem 1.5rem',
                            background: loading ? 'rgba(107, 114, 128, 0.3)' : 'rgba(59, 130, 246, 0.2)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            borderRadius: '0.5rem',
                            color: '#60a5fa',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          {loading ? '⏳ Checking...' : '🔄 Check Video Status'}
                        </button>
                        {generatingStates.all && (
                          <span style={{ padding: '0.75rem', color: '#fbbf24' }}>
                            ⏳ Generating... This takes 3-5 minutes per video
                          </span>
                        )}
                      </div>
                      
                      {/* Progress indicator */}
                      {scenes.length > 0 && (
                        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Progress</span>
                            <span style={{ fontSize: '0.85rem', color: '#22c55e' }}>
                              {scenes.filter(s => s.video_url).length} / {scenes.length} complete
                            </span>
                          </div>
                          <div style={{ height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                            <div 
                              style={{ 
                                height: '100%', 
                                width: `${(scenes.filter(s => s.video_url).length / scenes.length) * 100}%`,
                                background: 'linear-gradient(90deg, #22c55e, #10b981)',
                                borderRadius: '3px',
                                transition: 'width 0.3s ease'
                              }} 
                            />
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                        {scenes.map((scene, index) => (
                          <div key={scene.id} style={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', padding: '0.5rem' }}>
                            <div style={{ fontSize: '0.75rem', marginBottom: '0.5rem', color: '#94a3b8' }}>Scene {index + 1}</div>
                            
                            {scene.source_url && (
                              <img src={scene.source_url} alt={`Scene ${index + 1}`} style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '0.5rem' }} />
                            )}
                            
                            <div style={{ padding: '0.5rem 0', textAlign: 'center', fontSize: '0.8rem' }}>
                              {scene.video_url ? (
                                <span style={{ color: '#22c55e' }}>✅ Video Ready</span>
                              ) : generatingStates[scene.id] ? (
                                <span style={{ color: '#f59e0b' }}>⏳ Generating...</span>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>⏸️ Pending</span>
                              )}
                            </div>
                            
                            {scene.video_url && (
                              <video src={scene.video_url} controls style={{ width: '100%', borderRadius: '0.5rem', marginTop: '0.5rem' }} />
                            )}
                            
                            {!scene.video_url && !generatingStates[scene.id] && (
                              <button
                                onClick={() => {
                                  const prompt = window.prompt('Motion prompt:', 'Subtle cinematic camera movement');
                                  if (prompt) generateSceneVideo(scene.id, prompt);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  background: 'rgba(245, 158, 11, 0.2)',
                                  border: '1px solid rgba(245, 158, 11, 0.3)',
                                  borderRadius: '0.5rem',
                                  color: '#fbbf24',
                                  cursor: 'pointer',
                                  marginTop: '0.5rem',
                                  fontSize: '0.8rem'
                                }}
                              >
                                🎬 Generate
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* PREVIEW TAB */}
                  {multiTab === 'preview' && (
                    <div>
                      <h3 style={{ margin: '0 0 1rem' }}>🎥 Video Preview</h3>
                      
                      {/* Final Merged Video */}
                      {finalVideoUrl && (
                        <div style={{ 
                          marginBottom: '1.5rem', 
                          padding: '1.5rem', 
                          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
                          borderRadius: '1rem',
                          border: '2px solid rgba(34, 197, 94, 0.5)'
                        }}>
                          <h4 style={{ margin: '0 0 1rem', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            ✅ Final Video Ready!
                          </h4>
                          <video 
                            src={finalVideoUrl} 
                            controls 
                            style={{ width: '100%', maxHeight: '400px', borderRadius: '0.75rem', marginBottom: '1rem' }} 
                          />
                          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <a
                              href={finalVideoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                border: 'none',
                                borderRadius: '0.5rem',
                                color: 'white',
                                textDecoration: 'none',
                                fontWeight: '600',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                            >
                              📥 Download Final Video
                            </a>
                            <button
                              onClick={() => navigator.clipboard.writeText(finalVideoUrl).then(() => alert('URL copied!'))}
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: 'rgba(59, 130, 246, 0.2)',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                borderRadius: '0.5rem',
                                color: '#60a5fa',
                                cursor: 'pointer',
                                fontWeight: '500'
                              }}
                            >
                              📋 Copy URL
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Regenerate the final video?\n\nThis will create a new slideshow from your images and audio.')) {
                                  setFinalVideoUrl(null);
                                  createKenBurnsSlideshow();
                                }
                              }}
                              disabled={merging}
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: merging ? 'rgba(107, 114, 128, 0.3)' : 'rgba(245, 158, 11, 0.2)',
                                border: '1px solid rgba(245, 158, 11, 0.3)',
                                borderRadius: '0.5rem',
                                color: '#fbbf24',
                                cursor: merging ? 'not-allowed' : 'pointer',
                                fontWeight: '500'
                              }}
                            >
                              {merging ? '⏳ Regenerating...' : '🔄 Regenerate Video'}
                            </button>
                          </div>
                          
                          {/* Publish to Learning Library */}
                          <div style={{ 
                            marginTop: '1rem', 
                            paddingTop: '1rem', 
                            borderTop: '1px solid rgba(255,255,255,0.1)' 
                          }}>
                            <h5 style={{ margin: '0 0 0.5rem', color: '#a78bfa', fontSize: '0.9rem' }}>
                              📚 Publish to Learning Library
                            </h5>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                              <select
                                id="publish-category"
                                defaultValue="platform-overview"
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  background: 'rgba(15, 23, 42, 0.8)',
                                  border: '1px solid rgba(139, 92, 246, 0.3)',
                                  borderRadius: '0.375rem',
                                  color: 'white',
                                  fontSize: '0.85rem'
                                }}
                              >
                                <option value="platform-overview">🎯 Platform Overview</option>
                                <option value="feature-tutorials">📚 Feature Tutorials</option>
                                <option value="market-analysis">📊 Market Analysis</option>
                                <option value="sentiment-analysis">💭 Sentiment Analysis</option>
                                <option value="trading-strategies">📈 Trading Strategies</option>
                                <option value="getting-started">🚀 Getting Started</option>
                                <option value="tips-tricks">💡 Tips & Tricks</option>
                                <option value="product-updates">🆕 Product Updates</option>
                              </select>
                              <button
                                onClick={async () => {
                                  const category = document.getElementById('publish-category').value;
                                  if (!window.confirm(`Publish this video to Learning Library?\n\nCategory: ${category}`)) return;
                                  
                                  try {
                                    const res = await fetch(`${API_BASE}/video/projects/${currentProject.project_id}/publish`, {
                                      method: 'POST',
                                      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        title: currentProject.title,
                                        description: currentProject.topic,
                                        category: category
                                      })
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      alert(`✅ ${data.message}\n\nVideo ID: ${data.videoId}`);
                                    } else {
                                      throw new Error(data.error || 'Publish failed');
                                    }
                                  } catch (error) {
                                    alert('Failed to publish: ' + error.message);
                                  }
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                  border: 'none',
                                  borderRadius: '0.375rem',
                                  color: 'white',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  fontSize: '0.85rem'
                                }}
                              >
                                📤 Publish to Library
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Ken Burns Slideshow - RECOMMENDED (No AI, No Gibberish) */}
                      {!finalVideoUrl && scenes.length > 0 && (
                        <div style={{ 
                          marginBottom: '1.5rem', 
                          padding: '1.25rem', 
                          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.1) 100%)',
                          borderRadius: '0.75rem',
                          border: '2px solid rgba(34, 197, 94, 0.4)'
                        }}>
                          <h4 style={{ margin: '0 0 0.75rem', color: '#22c55e' }}>✨ Create Clean Slideshow (Recommended)</h4>
                          <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#94a3b8' }}>
                            Create a professional video with Ken Burns effects (subtle zoom/pan) from your {scenes.length} images
                            {currentProject.narration_audio_url && ' + voice narration'}. 
                            <strong style={{ color: '#22c55e' }}> No AI animation = No gibberish!</strong>
                          </p>
                          <button
                            onClick={createKenBurnsSlideshow}
                            disabled={merging}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: merging ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #22c55e, #16a34a)',
                              border: 'none',
                              borderRadius: '0.5rem',
                              color: 'white',
                              cursor: merging ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              fontSize: '1rem'
                            }}
                          >
                            {merging ? '⏳ Creating Slideshow...' : '✨ Create Ken Burns Slideshow'}
                          </button>
                        </div>
                      )}

                      {/* Merge AI Videos Button (if videos exist) */}
                      {!finalVideoUrl && scenes.filter(s => s.video_url).length > 0 && (
                        <div style={{ 
                          marginBottom: '1.5rem', 
                          padding: '1.25rem', 
                          background: 'rgba(245, 158, 11, 0.1)',
                          borderRadius: '0.75rem',
                          border: '1px solid rgba(245, 158, 11, 0.3)'
                        }}>
                          <h4 style={{ margin: '0 0 0.75rem', color: '#fbbf24' }}>🎬 Or Merge AI-Generated Videos</h4>
                          <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#94a3b8' }}>
                            Merge the {scenes.filter(s => s.video_url).length} Kling AI videos you generated
                            {currentProject.narration_audio_url && ' with voice narration'}.
                          </p>
                          <button
                            onClick={mergeVideosWithAudio}
                            disabled={merging}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: merging ? 'rgba(107, 114, 128, 0.3)' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                              border: 'none',
                              borderRadius: '0.5rem',
                              color: 'white',
                              cursor: merging ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              fontSize: '1rem'
                            }}
                          >
                            {merging ? '⏳ Merging Videos...' : '🎬 Merge AI Videos'}
                          </button>
                        </div>
                      )}
                      
                      {currentProject.narration_audio_url && (
                        <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '0.75rem' }}>
                          <h4 style={{ margin: '0 0 0.75rem' }}>🎧 Background Narration</h4>
                          <audio controls src={currentProject.narration_audio_url} style={{ width: '100%' }} />
                        </div>
                      )}
                      
                      {/* Individual Scenes */}
                      <h4 style={{ margin: '1.5rem 0 1rem', color: '#94a3b8' }}>📷 Individual Scenes</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {scenes.map((scene, index) => (
                          <div key={scene.id} style={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.75rem', padding: '1rem' }}>
                            <h4 style={{ margin: '0 0 0.75rem' }}>Scene {index + 1}</h4>
                            
                            {scene.video_url ? (
                              <video src={scene.video_url} controls style={{ width: '100%', maxHeight: '300px', borderRadius: '0.5rem' }} />
                            ) : scene.source_url ? (
                              <img src={scene.source_url} alt={`Scene ${index + 1}`} style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '0.5rem' }} />
                            ) : null}
                            
                            {scene.narration_text && (
                              <p style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '0.5rem', fontSize: '0.9rem' }}>
                                🎤 {scene.narration_text}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {scenes.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                          Upload images first to preview your video.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Tips */}
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '0.75rem',
          fontSize: '0.8rem',
          color: '#93c5fd'
        }}>
          <h4 style={{ margin: '0 0 0.5rem' }}>💡 Tips for Best Results</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div>
              <strong>📸 Single Image Mode:</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                <li>Best for quick one-off marketing videos</li>
                <li>High-res screenshots work best</li>
                <li>Each video takes 3-5 minutes</li>
              </ul>
            </div>
            <div>
              <strong>🎬 Multi-Image + Voice Mode:</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                <li>Upload multiple screenshots in order</li>
                <li>Generate AI narration script first</li>
                <li>Create voice audio with ElevenLabs</li>
                <li>Generate videos for each scene</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingVideoAdmin;